import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../api';
import { disconnectSocket } from '../hooks/useSocket';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // "Login as user": the admin's own token is parked in localStorage while
  // impersonating, so they can return without re-entering credentials.
  const [impersonating, setImpersonating] = useState(!!localStorage.getItem('admin_token'));

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { setLoading(false); return; }
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    api.get('/auth/me')
      .then(r => setUser(r.data.user))
      .catch(async err => {
        if (err.response?.data?.error === 'workspace_access_revoked') {
          localStorage.removeItem('active_workspace');
          const r = await api.get('/auth/me');
          setUser(r.data.user);
        } else {
          localStorage.removeItem('token');
          delete api.defaults.headers.common['Authorization'];
        }
      })
      .catch(() => { localStorage.removeItem('token'); delete api.defaults.headers.common['Authorization']; })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const revoked = () => {
      disconnectSocket();
      api.get('/auth/me').then(r => setUser(r.data.user)).catch(() => {});
    };
    window.addEventListener('workspace:revoked', revoked);
    return () => window.removeEventListener('workspace:revoked', revoked);
  }, []);

  function login(token, userData) {
    localStorage.setItem('active_workspace', String(userData.workspace_owner || userData._id));
    localStorage.setItem('token', token);
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
    api.get('/auth/me').then(r => setUser(r.data.user)).catch(() => {});
  }

  function logout() {
    localStorage.removeItem('token');
    localStorage.removeItem('admin_token'); // a full logout also ends impersonation
    localStorage.removeItem('active_workspace');
    localStorage.removeItem('admin_workspace');
    delete api.defaults.headers.common['Authorization'];
    // Drop the live socket so the next login authenticates with its own token
    disconnectSocket();
    setUser(null);
    setImpersonating(false);
  }

  // Switch to a user's session, keeping the admin's token to come back to.
  function impersonate(token, userData) {
    const current = localStorage.getItem('token');
    if (current) localStorage.setItem('admin_token', current);
    const workspace = localStorage.getItem('active_workspace');
    if (workspace) localStorage.setItem('admin_workspace', workspace);
    disconnectSocket(); // reconnect with the impersonated user's token
    login(token, userData);
    setImpersonating(true);
  }

  // Restore the parked admin session.
  async function returnToAdmin() {
    const adminToken = localStorage.getItem('admin_token');
    if (!adminToken) return;
    localStorage.removeItem('admin_token');
    localStorage.setItem('token', adminToken);
    const workspace = localStorage.getItem('admin_workspace');
    if (workspace) localStorage.setItem('active_workspace', workspace);
    else localStorage.removeItem('active_workspace');
    localStorage.removeItem('admin_workspace');
    api.defaults.headers.common['Authorization'] = `Bearer ${adminToken}`;
    disconnectSocket();
    setImpersonating(false);
    try {
      const r = await api.get('/auth/me');
      setUser(r.data.user);
    } catch {
      logout(); // admin token expired while impersonating — sign in again
    }
  }

  function refreshUser() {
    return api.get('/auth/me').then(r => setUser(r.data.user));
  }

  async function switchWorkspace(id) {
    const previous = localStorage.getItem('active_workspace');
    localStorage.setItem('active_workspace', String(id));
    disconnectSocket();
    try {
      const r = await api.get('/auth/me');
      setUser(r.data.user);
      window.location.assign('/dashboard');
    } catch (err) {
      if (previous) localStorage.setItem('active_workspace', previous);
      else localStorage.removeItem('active_workspace');
      throw err;
    }
  }

  return (
    <AuthContext.Provider value={{ user, login, logout, refreshUser, switchWorkspace, loading, impersonate, returnToAdmin, impersonating }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
